import io from 'socket.io-client';

class WebSocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.listeners = new Map();
  }

  // Connect to WebSocket server
  connect(serverUrl, authToken, namespace = '') {
    if (this.socket && this.isConnected) {
      return Promise.resolve();
    }

    // For Socket.IO, we connect to: serverUrl + namespace
    // Socket.IO will automatically use /socket.io/ as the path
    const connectionUrl = namespace ? `${serverUrl}${namespace}` : serverUrl;
    
    return new Promise((resolve, reject) => {
      try {
        // Connect to Socket.IO server with namespace
        this.socket = io(connectionUrl, {
          path: '/socket.io/',
          auth: {
            token: authToken
          },
          autoConnect: true,
          reconnection: true,
          reconnectionAttempts: 5,
          reconnectionDelay: 1000,
          reconnectionDelayMax: 5000,
          timeout: 20000,
          transports: ['websocket', 'polling'],
          upgrade: true,
          rememberUpgrade: true
        });

        // Connection successful
        this.socket.on('connect', () => {
          this.isConnected = true;
          resolve();
        });

        // Authentication confirmation
        this.socket.on('connected', (data) => {
          // Silent - authenticated successfully
        });

        // Connection error
        this.socket.on('connect_error', (error) => {
          console.error('❌ WebSocket connection error:', error);
          console.error('🔍 Error details:', {
            message: error.message,
            description: error.description,
            context: error.context
          });
          this.isConnected = false;
          reject(new Error(`Connection failed: ${error.message}`));
        });

        // Disconnection
        this.socket.on('disconnect', (reason) => {
          this.isConnected = false;
          
          // Auto-reconnect if server disconnected
          if (reason === 'io server disconnect') {
            this.socket.connect();
          }
        });

        // Reconnection successful
        this.socket.on('reconnect', (attemptNumber) => {
          this.isConnected = true;
        });

        // Reconnection failed
        this.socket.on('reconnect_error', (error) => {
          console.error('🔄❌ WebSocket reconnection failed:', error);
        });

      } catch (error) {
        console.error('WebSocket initialization error:', error);
        reject(error);
      }
    });
  }

  // Disconnect from WebSocket
  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
      this.listeners.clear();
    }
  }

  // Emit event to server
  emit(event, data) {
    if (this.socket && this.isConnected) {
      this.socket.emit(event, data);
    } else {
      console.error('⚠️ Cannot emit - WebSocket not connected:', event);
    }
  }

  // Listen for events from server
  on(event, callback) {
    if (this.socket) {
      this.socket.on(event, callback);
      
      // Store callback for cleanup
      if (!this.listeners.has(event)) {
        this.listeners.set(event, []);
      }
      this.listeners.get(event).push(callback);
    }
  }

  // Remove event listener
  off(event, callback) {
    if (this.socket) {
      this.socket.off(event, callback);
      
      // Remove from stored listeners
      if (this.listeners.has(event)) {
        const callbacks = this.listeners.get(event);
        const index = callbacks.indexOf(callback);
        if (index > -1) {
          callbacks.splice(index, 1);
        }
      }
    }
  }

  // Join a room (for job-specific updates)
  joinRoom(roomId) {
    if (!this.socket || !this.isConnected) {
      console.error('❌ Cannot join room - WebSocket not connected');
      return Promise.reject(new Error('WebSocket not connected'));
    }

    return new Promise((resolve) => {
      // Listen for joined confirmation (one-time)
      const onJoined = (data) => {
        this.socket.off('joined_job', onJoined);
        resolve(data);
      };
      
      this.socket.once('joined_job', onJoined);
      
      // Set timeout in case no confirmation (2 seconds)
      setTimeout(() => {
        this.socket.off('joined_job', onJoined);
        resolve({ roomId, timeout: true });
      }, 2000);
      
      // Emit join request (simple format: { jobId: roomId })
      this.socket.emit('join_job', { jobId: roomId });
    });
  }

  // Leave a room
  leaveRoom(roomId) {
    this.emit('leave_job', { jobId: roomId });
  }

  // Get connection status
  getStatus() {
    return {
      isConnected: this.isConnected,
      socketId: this.socket?.id || null,
      transport: this.socket?.io?.engine?.transport?.name || null
    };
  }
}

// Create singleton instance
const webSocketService = new WebSocketService();
export default webSocketService;